import React from 'react';
import { Typography, TypographyProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

/**
 * ResponsiveTypography is a simplified wrapper around Material UI's Typography component
 * that provides responsive font sizes based on screen size
 */
const ResponsiveTypography: React.FC<TypographyProps> = (props) => {
  const { children, variant = 'body1', sx = {}, ...rest } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Define responsive styles based on device size
  const mobileStyles = {
    fontSize: {
      h1: '2rem',
      h2: '1.75rem',
      h3: '1.5rem',
      h4: '1.35rem',
      h5: '1.15rem',
      h6: '1rem',
      subtitle1: '0.95rem',
      subtitle2: '0.85rem',
      body1: '0.95rem',
      body2: '0.85rem',
    },
    lineHeight: 1.5,
  };
  
  const tabletStyles = {
    fontSize: {
      h1: '2.25rem',
      h2: '1.85rem',
      h3: '1.65rem',
      h4: '1.45rem',
      h5: '1.25rem',
      h6: '1.1rem',
      subtitle1: '1rem',
      subtitle2: '0.9rem',
      body1: '1rem',
      body2: '0.9rem',
    },
    lineHeight: 1.3,
  };
  
  // Determine which styles to apply
  let responsiveStyles = {};
  if (isMobile && variant && mobileStyles.fontSize[variant as keyof typeof mobileStyles.fontSize]) {
    responsiveStyles = {
      fontSize: mobileStyles.fontSize[variant as keyof typeof mobileStyles.fontSize],
      lineHeight: mobileStyles.lineHeight,
    };
  } else if (isTablet && variant && tabletStyles.fontSize[variant as keyof typeof tabletStyles.fontSize]) {
    responsiveStyles = {
      fontSize: tabletStyles.fontSize[variant as keyof typeof tabletStyles.fontSize],
      lineHeight: tabletStyles.lineHeight,
    };
  }
  
  // Simplify by using object spread and letting MUI handle the typing
  const mergedSx = { ...responsiveStyles, ...sx };
  
  return (
    <Typography variant={variant} sx={mergedSx} {...rest}>
      {children}
    </Typography>
  );
};

export default ResponsiveTypography; 